import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Toaster } from '@/components/ui/sonner';

import GrandEntrance from '@/components/GrandEntrance';
import SpatialLobby from '@/components/SpatialLobby';
import RoomInterior from '@/components/RoomInterior';
import DebateStage from '@/components/DebateStage';
import RoomRequestModal from '@/components/RoomRequestModal';
import AddMindModal from '@/components/AddMindModal';
import { getStaticRooms, getStaticMindsByRoom } from '@/config/staticMuseumData';
import { ROOM_ORDER } from '@/config/roomThemes';

const API = process.env.REACT_APP_BACKEND_URL;
const requiredRoomIds = new Set(ROOM_ORDER);

const hasUsableRooms = (rooms) => (
  Array.isArray(rooms) &&
  rooms.length > 0 &&
  ROOM_ORDER.some(id => rooms.some(room => room?.id === id)) &&
  rooms.every(room => typeof room?.id === 'string' && typeof room?.name === 'string')
);

const hasUsableMinds = (minds, roomId) => (
  Array.isArray(minds) &&
  minds.length > 0 &&
  minds.every(mind => (
    typeof mind?.id === 'string' &&
    typeof mind?.name === 'string' &&
    (!mind.room_id || mind.room_id === roomId)
  ))
);

function App() {
  const [view, setView] = useState('entrance'); // entrance | hallway | room | debate
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [minds, setMinds] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [loadingMinds, setLoadingMinds] = useState(false);
  const [backendStatus, setBackendStatus] = useState('checking'); // checking | online | waking | offline
  const [showRoomRequest, setShowRoomRequest] = useState(false);
  const [showAddMind, setShowAddMind] = useState(false);
  // Debate state
  const [debateData, setDebateData] = useState(null);
  const [debateMind1, setDebateMind1] = useState(null);
  const [debateMind2, setDebateMind2] = useState(null);

  useEffect(() => { fetchRooms(); }, []);
  useEffect(() => { if (selectedRoom) fetchMinds(selectedRoom.id); }, [selectedRoom]);

  const fetchRooms = async () => {
    setLoadingRooms(true);
    setRooms(getStaticRooms());
    if (!API) {
      setBackendStatus('offline');
      setLoadingRooms(false);
      return;
    }

    try {
      const res = await fetch(`${API}/api/rooms`);
      if (!res.ok) throw new Error(`Rooms request failed: ${res.status}`);
      const liveRooms = await res.json();
      if (!hasUsableRooms(liveRooms)) throw new Error('Rooms response did not contain usable museum rooms');
      setRooms(liveRooms.filter(room => requiredRoomIds.has(room.id)));
      setBackendStatus('online');
    }
    catch (err) {
      console.warn('Using static rooms while the backend wakes or is unavailable:', err);
      setBackendStatus('waking');
    }
    finally { setLoadingRooms(false); }
  };

  const fetchMinds = async (roomId) => {
    setLoadingMinds(true);
    setMinds(getStaticMindsByRoom(roomId));
    if (!API) {
      setBackendStatus('offline');
      setLoadingMinds(false);
      return;
    }

    try {
      const res = await fetch(`${API}/api/rooms/${roomId}/minds`);
      if (!res.ok) throw new Error(`Minds request failed: ${res.status}`);
      const liveMinds = await res.json();
      if (!hasUsableMinds(liveMinds, roomId)) throw new Error(`Minds response for ${roomId} was empty or malformed`);
      setMinds(liveMinds);
      setBackendStatus('online');
    }
    catch (err) {
      console.warn('Using static minds while the backend wakes or is unavailable:', err);
      setBackendStatus(prev => prev === 'online' ? 'online' : 'waking');
    }
    finally { setLoadingMinds(false); }
  };

  const enterMuseum = () => setView('hallway');

  const selectRoom = (room) => {
    setSelectedRoom(room);
    setView('room');
  };

  const backToHallway = () => {
    setView('hallway');
    setSelectedRoom(null);
    setMinds([]);
  };

  const startDebate = async (mind1, mind2, topic) => {
    try {
      const res = await fetch(`${API}/api/debates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mind1_id: mind1.id, mind2_id: mind2.id, topic, room_id: selectedRoom.id }),
      });
      const data = await res.json();
      setDebateData({ ...data, topic });
      setDebateMind1(mind1);
      setDebateMind2(mind2);
      setView('debate');
    } catch (err) {
      console.error('Failed to start debate:', err);
    }
  };

  const exitDebate = () => {
    setView('room');
    setDebateData(null);
    setDebateMind1(null);
    setDebateMind2(null);
  };

  const handleMindAdded = () => {
    if (selectedRoom?.id) fetchMinds(selectedRoom.id);
  };

  return (
    <div className="min-h-screen" style={{ background: '#FDFCFB' }}>
      <AnimatePresence mode="wait">
        {view === 'entrance' && (
          <motion.div key="entrance" exit={{ opacity: 0 }} transition={{ duration: 0.6 }}>
            <GrandEntrance onEnter={enterMuseum} />
          </motion.div>
        )}
        {view === 'hallway' && (
          <motion.div key="hallway" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
            <SpatialLobby rooms={rooms} isLoading={loadingRooms} onSelectRoom={selectRoom} onRequestRoom={() => setShowRoomRequest(true)} />
            <BackendStatusToast status={backendStatus} />
          </motion.div>
        )}
        {view === 'room' && (
          <motion.div key="room" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
            <RoomInterior room={selectedRoom} minds={minds} isLoading={loadingMinds} onBack={backToHallway} onAddMind={() => setShowAddMind(true)} onStartDebate={startDebate} />
            <BackendStatusToast status={backendStatus} />
          </motion.div>
        )}
        {view === 'debate' && (
          <motion.div key="debate" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
            <DebateStage debate={debateData} mind1={debateMind1} mind2={debateMind2} room={selectedRoom} onClose={exitDebate} />
          </motion.div>
        )}
      </AnimatePresence>

      <RoomRequestModal isOpen={showRoomRequest} onClose={() => setShowRoomRequest(false)} />
      <AddMindModal isOpen={showAddMind} onClose={() => setShowAddMind(false)} roomId={selectedRoom?.id} onSuccess={handleMindAdded} />
      <Toaster position="bottom-right" />
    </div>
  );
}

const BackendStatusToast = ({ status }) => {
  if (status === 'online' || status === 'checking') return null;

  const copy = status === 'offline'
    ? 'Live AI is offline; exploring static museum data.'
    : 'Live AI is waking up; the museum is open in static mode.';

  return (
    <div
      className="fixed bottom-4 left-1/2 z-40 -translate-x-1/2 px-4 py-2 text-center font-body text-xs shadow-lg"
      style={{
        background: 'rgba(253,252,251,0.94)',
        border: '1px solid #E6E1DB',
        borderRadius: 999,
        color: '#5C4033',
        backdropFilter: 'blur(12px)',
      }}
    >
      {copy}
    </div>
  );
};

export default App;
